import { xEmbedFeaturesBase64 } from './xEmbedFeatures'

// Enhanced URL validation with domain allowlist for each platform
const ALLOWED_YOUTUBE_DOMAINS = ['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com']
const ALLOWED_VIMEO_DOMAINS = ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']
const ALLOWED_TIKTOK_DOMAINS = ['tiktok.com', 'www.tiktok.com', 'm.tiktok.com']
const ALLOWED_X_DOMAINS = [
  'x.com',
  'www.x.com',
  'twitter.com',
  'www.twitter.com',
  'mobile.twitter.com',
  'm.twitter.com',
]

export const isValidVideoUrl = (url) => {
  try {
    const parsed = new URL(url)
    const isYouTube = ALLOWED_YOUTUBE_DOMAINS.includes(parsed.hostname)
    const isVimeo = ALLOWED_VIMEO_DOMAINS.includes(parsed.hostname)
    const isTikTok = ALLOWED_TIKTOK_DOMAINS.includes(parsed.hostname)
    const isX = ALLOWED_X_DOMAINS.includes(parsed.hostname)

    return {
      isValid: isYouTube || isVimeo || isTikTok || isX,
      isYouTube,
      isVimeo,
      isTikTok,
      isX,
    }
  } catch {
    return {
      isValid: false,
      isYouTube: false,
      isVimeo: false,
      isTikTok: false,
      isX: false,
    }
  }
}

export const extractYoutubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^&\n?#]+)/)
  return match ? match[1] : null
}

export const extractVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/)
  return match ? match[1] : null
}

export const extractTikTokId = (url) => {
  // TikTok URLs: only accept full URLs like tiktok.com/@user/video/123456789
  // Short URLs (vm.tiktok.com) are not supported for embedding
  const videoMatch = url.match(/\/video\/(\d+)/)
  return videoMatch ? videoMatch[1] : null
}

export const extractXId = (url) => {
  const match = url.match(/\/status\/(\d+)/)
  return match ? match[1] : null
}

export const getPlatformFromUrl = (url) => {
  const validation = isValidVideoUrl(url)

  if (validation.isYouTube) return 'youtube'
  if (validation.isVimeo) return 'vimeo'
  if (validation.isTikTok) return 'tiktok'
  if (validation.isX) return 'x'

  return 'other'
}

export const extractPlatformId = (url, platform) => {
  switch (platform) {
    case 'youtube': return extractYoutubeId(url)
    case 'vimeo': return extractVimeoId(url)
    case 'tiktok': return extractTikTokId(url)
    case 'x': return extractXId(url)
    default: return null
  }
}

export const normalizeUrl = (url) => {
  const { isValid } = isValidVideoUrl(url)

  if (!isValid) {
    return url
  }

  const platform = getPlatformFromUrl(url)
  const videoId = extractPlatformId(url, platform)

  // Normalize URLs to standard format
  switch (platform) {
    case 'youtube':
      // Convert youtu.be and other formats to standard youtube.com
      return videoId ? `https://www.youtube.com/watch?v=${videoId}` : url
    case 'vimeo':
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url
    case 'tiktok':
      return url
    case 'x':
      return videoId ? `https://x.com/i/status/${videoId}` : url
    default:
      return url
  }
}

export const convertToEmbedUrl = (url, externalId, source, embedId) => {
  const isValid = Boolean(source && externalId) || isValidVideoUrl(url).isValid

  if (!isValid) {
    return url
  }

  const platform = source || getPlatformFromUrl(url)
  const videoId = externalId || extractPlatformId(url, platform)

  switch (platform) {
    case 'youtube':
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url
    case 'vimeo':
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url
    case 'tiktok': {
      // Use TikTok Player API v1 with optimized settings for embedded display
      if (!videoId) return url
      const params = new URLSearchParams({
        music_info: '0',        // Hide music info
        description: '0',       // Hide description
        controls: '0',          // Hide playback controls
        rel: '0',               // Show current author's videos, not recommendations
      })
      return `https://www.tiktok.com/player/v1/${videoId}?${params.toString()}`
    }
    case 'x': {
      if (!videoId) return url
      const params = new URLSearchParams({
        id: videoId,
        dnt: 'false',
        embedId,
        features: xEmbedFeaturesBase64,
        frame: 'false',
        hideCard: 'true',
        hideThread: 'true',
        lang: 'en',
        maxWidth: '100%',
        origin: 'https://publish.twitter.com/#',
        theme: 'light',
        widgetsVersion: '2615f7e52b7e0:1702314776716',
        width: '100%',
      })
      return `https://platform.twitter.com/embed/Tweet.html?${params.toString()}`
    }
    default:
      return url
  }
}
